/**
 * Catálogo de alimentos corrientes con su grupo.
 *
 * Va en el paquete compartido y no en la base de datos a propósito: es lo que
 * permite que al escribir "tomate" la app sepa que es verdura sin pedir red.
 * Los grupos y los pesos por unidad salen de USDA FoodData Central; el
 * servidor consulta allí sólo lo que no esté aquí, que es la cola larga.
 *
 * `gramsPerUnit` es el peso de una pieza mediana. Sin él, una receta con "2
 * unidades" no aporta gramos y el plato lo dice en vez de inventárselos.
 */
import { normaliseName, type FoodGroup, type ShoppingSection } from './events.js'

export interface CatalogFood {
  name: string
  group: FoodGroup
  /** Peso de una pieza mediana, en gramos. */
  gramsPerUnit?: number
  /** Sólo cuando la sección no es la que le tocaría por su grupo. */
  section?: ShoppingSection
}

export const FOOD_CATALOG: readonly CatalogFood[] = [
  // Verduras y hortalizas
  { name: 'Tomate', group: 'verdura', gramsPerUnit: 120 },
  { name: 'Cebolla', group: 'verdura', gramsPerUnit: 150 },
  { name: 'Ajo', group: 'verdura', gramsPerUnit: 5 },
  { name: 'Patata', group: 'verdura', gramsPerUnit: 180 },
  { name: 'Zanahoria', group: 'verdura', gramsPerUnit: 80 },
  { name: 'Pimiento', group: 'verdura', gramsPerUnit: 150 },
  { name: 'Calabacín', group: 'verdura', gramsPerUnit: 250 },
  { name: 'Berenjena', group: 'verdura', gramsPerUnit: 250 },
  { name: 'Brócoli', group: 'verdura', gramsPerUnit: 400 },
  { name: 'Coliflor', group: 'verdura', gramsPerUnit: 600 },
  { name: 'Judía verde', group: 'verdura' },
  { name: 'Guisantes', group: 'verdura' },
  { name: 'Espinacas', group: 'verdura' },
  { name: 'Acelgas', group: 'verdura' },
  { name: 'Lechuga', group: 'verdura', gramsPerUnit: 300 },
  { name: 'Canónigos', group: 'verdura' },
  { name: 'Rúcula', group: 'verdura' },
  { name: 'Pepino', group: 'verdura', gramsPerUnit: 200 },
  { name: 'Champiñones', group: 'verdura' },
  { name: 'Setas', group: 'verdura' },
  { name: 'Puerro', group: 'verdura', gramsPerUnit: 120 },
  { name: 'Apio', group: 'verdura' },
  { name: 'Calabaza', group: 'verdura' },
  { name: 'Alcachofa', group: 'verdura', gramsPerUnit: 120 },
  { name: 'Espárragos', group: 'verdura' },
  { name: 'Col', group: 'verdura' },
  { name: 'Repollo', group: 'verdura' },
  { name: 'Remolacha', group: 'verdura', gramsPerUnit: 150 },
  { name: 'Boniato', group: 'verdura', gramsPerUnit: 200 },
  { name: 'Maíz dulce', group: 'verdura' },
  { name: 'Tomate frito', group: 'verdura', section: 'despensa' },
  { name: 'Tomate triturado', group: 'verdura', section: 'despensa' },

  // Frutas
  { name: 'Manzana', group: 'fruta', gramsPerUnit: 180 },
  { name: 'Plátano', group: 'fruta', gramsPerUnit: 120 },
  { name: 'Naranja', group: 'fruta', gramsPerUnit: 200 },
  { name: 'Mandarina', group: 'fruta', gramsPerUnit: 90 },
  { name: 'Limón', group: 'fruta', gramsPerUnit: 100 },
  { name: 'Pera', group: 'fruta', gramsPerUnit: 180 },
  { name: 'Fresas', group: 'fruta' },
  { name: 'Uvas', group: 'fruta' },
  { name: 'Melón', group: 'fruta' },
  { name: 'Sandía', group: 'fruta' },
  { name: 'Melocotón', group: 'fruta', gramsPerUnit: 150 },
  { name: 'Kiwi', group: 'fruta', gramsPerUnit: 80 },
  { name: 'Piña', group: 'fruta' },
  { name: 'Aguacate', group: 'fruta', gramsPerUnit: 200 },
  { name: 'Ciruela', group: 'fruta', gramsPerUnit: 70 },
  { name: 'Cereza', group: 'fruta' },
  { name: 'Arándanos', group: 'fruta' },
  { name: 'Higo', group: 'fruta', gramsPerUnit: 50 },
  { name: 'Granada', group: 'fruta', gramsPerUnit: 280 },

  // Carne
  { name: 'Pollo', group: 'carne' },
  { name: 'Pechuga de pollo', group: 'carne', gramsPerUnit: 150 },
  { name: 'Muslo de pollo', group: 'carne', gramsPerUnit: 130 },
  { name: 'Pavo', group: 'carne' },
  { name: 'Ternera', group: 'carne' },
  { name: 'Cerdo', group: 'carne' },
  { name: 'Lomo de cerdo', group: 'carne' },
  { name: 'Carne picada', group: 'carne' },
  { name: 'Conejo', group: 'carne' },
  { name: 'Cordero', group: 'carne' },
  { name: 'Jamón serrano', group: 'carne' },
  { name: 'Jamón cocido', group: 'carne' },
  { name: 'Pavo en lonchas', group: 'carne' },
  { name: 'Chorizo', group: 'carne' },
  { name: 'Bacón', group: 'carne' },
  { name: 'Salchichas', group: 'carne' },
  { name: 'Costillas', group: 'carne' },

  // Pescado y marisco
  { name: 'Merluza', group: 'pescado' },
  { name: 'Salmón', group: 'pescado' },
  { name: 'Atún', group: 'pescado' },
  { name: 'Atún en lata', group: 'pescado', gramsPerUnit: 52, section: 'despensa' },
  { name: 'Bacalao', group: 'pescado' },
  { name: 'Dorada', group: 'pescado', gramsPerUnit: 400 },
  { name: 'Lubina', group: 'pescado', gramsPerUnit: 400 },
  { name: 'Sardinas', group: 'pescado' },
  { name: 'Boquerones', group: 'pescado' },
  { name: 'Lenguado', group: 'pescado' },
  { name: 'Rape', group: 'pescado' },
  { name: 'Gambas', group: 'pescado' },
  { name: 'Langostinos', group: 'pescado' },
  { name: 'Mejillones', group: 'pescado' },
  { name: 'Calamares', group: 'pescado' },
  { name: 'Pulpo', group: 'pescado' },
  { name: 'Almejas', group: 'pescado' },

  // Huevos
  { name: 'Huevo', group: 'huevo', gramsPerUnit: 60 },
  { name: 'Clara de huevo', group: 'huevo', gramsPerUnit: 33 },

  // Legumbres
  { name: 'Lentejas', group: 'legumbre' },
  { name: 'Garbanzos', group: 'legumbre' },
  { name: 'Alubias', group: 'legumbre' },
  { name: 'Judías blancas', group: 'legumbre' },
  { name: 'Soja', group: 'legumbre' },
  { name: 'Tofu', group: 'legumbre', section: 'lacteos' },
  { name: 'Hummus', group: 'legumbre', section: 'lacteos' },
  { name: 'Guisantes secos', group: 'legumbre' },

  // Cereales y tubérculos de despensa
  { name: 'Arroz', group: 'cereal' },
  { name: 'Pasta', group: 'cereal' },
  { name: 'Macarrones', group: 'cereal' },
  { name: 'Espaguetis', group: 'cereal' },
  { name: 'Fideos', group: 'cereal' },
  { name: 'Pan', group: 'cereal', gramsPerUnit: 250, section: 'panaderia' },
  { name: 'Pan de molde', group: 'cereal', gramsPerUnit: 30, section: 'panaderia' },
  { name: 'Harina', group: 'cereal' },
  { name: 'Avena', group: 'cereal' },
  { name: 'Cuscús', group: 'cereal' },
  { name: 'Quinoa', group: 'cereal' },
  { name: 'Tortillas de trigo', group: 'cereal', gramsPerUnit: 40, section: 'panaderia' },
  { name: 'Pan rallado', group: 'cereal' },
  { name: 'Cereales de desayuno', group: 'cereal' },
  { name: 'Galletas', group: 'cereal' },
  { name: 'Picos', group: 'cereal', section: 'panaderia' },

  // Lácteos
  { name: 'Leche', group: 'lacteo' },
  { name: 'Yogur', group: 'lacteo', gramsPerUnit: 125 },
  { name: 'Yogur griego', group: 'lacteo', gramsPerUnit: 150 },
  { name: 'Queso fresco', group: 'lacteo' },
  { name: 'Queso curado', group: 'lacteo' },
  { name: 'Queso rallado', group: 'lacteo' },
  { name: 'Mozzarella', group: 'lacteo', gramsPerUnit: 125 },
  { name: 'Nata', group: 'lacteo' },
  { name: 'Cuajada', group: 'lacteo', gramsPerUnit: 125 },
  { name: 'Requesón', group: 'lacteo' },
  { name: 'Mantequilla', group: 'grasa', section: 'lacteos' },

  // Grasas y frutos secos
  { name: 'Aceite de oliva', group: 'grasa' },
  { name: 'Aceite de girasol', group: 'grasa' },
  { name: 'Aceitunas', group: 'grasa' },
  { name: 'Almendras', group: 'grasa' },
  { name: 'Nueces', group: 'grasa' },
  { name: 'Anacardos', group: 'grasa' },
  { name: 'Cacahuetes', group: 'grasa' },
  { name: 'Mayonesa', group: 'grasa' },
  { name: 'Semillas de sésamo', group: 'grasa' },
] as const

const byName = new Map<string, CatalogFood>(
  FOOD_CATALOG.map((food) => [normaliseName(food.name), food]),
)

/** El alimento exacto con ese nombre, si está en el catálogo. */
export function findFood(name: string): CatalogFood | null {
  return byName.get(normaliseName(name)) ?? null
}

/**
 * Busca por trozo de nombre, primero lo que empieza igual.
 *
 * Es lo que rellena el grupo mientras se escribe el ingrediente: sin red, sin
 * esperar, y sin que nadie tenga que decidir si el atún es carne o pescado.
 */
export function searchFoods(query: string, limit = 8): CatalogFood[] {
  const needle = normaliseName(query)
  if (!needle) return []

  const starts: CatalogFood[] = []
  const contains: CatalogFood[] = []
  for (const food of FOOD_CATALOG) {
    const name = normaliseName(food.name)
    if (name.startsWith(needle)) starts.push(food)
    else if (name.includes(needle)) contains.push(food)
    if (starts.length >= limit) break
  }
  return [...starts, ...contains].slice(0, limit)
}

/**
 * Los gramos que suponen `quantity` unidades de ese alimento, si se sabe lo
 * que pesa una pieza. Es lo que convierte "2 huevos" en 120 g sin preguntar.
 */
export function gramsForUnits(name: string, quantity: number): number | null {
  const food = findFood(name)
  return food?.gramsPerUnit ? Math.round(food.gramsPerUnit * quantity) : null
}
